import { useEffect, useState } from "react";
import { Tv, Lightbulb, Monitor, Radio, Volume1, Volume2, VolumeX, Home as HomeIcon, Undo2, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, CircleDot, Power, Moon, Play, Activity, AppWindow } from "lucide-react";
import { api } from "@/lib/api";
import { useLive } from "@/context/LiveContext";
import { StatusBadge } from "./StatusBadge";
import { TvRemote } from "./TvRemote";
import { PcRemote } from "./PcRemote";
import { YouTubeMusic } from "./YouTubeMusic";
import { OperationsPanels } from "./OperationsPanels";
import TradingOS from "@/pages/TradingOS";

function Tile({ icon: Icon, title, status, detail, enabled, reason, children, testId }) {
  return (
    <section data-testid={testId} className={`hud-panel p-3 transition-colors ${enabled ? "border-cyan-500/40" : "opacity-80"}`}>
      <header className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2"><Icon className={`h-4 w-4 ${enabled ? "text-cyan-300" : "text-slate-500"}`} /><span className="font-hud text-xs tracking-widest">{title}</span></div>
        <StatusBadge status={status} testId={`${testId}-status`} />
      </header>
      {detail && <p className="mt-1 text-[10px] text-slate-500 line-clamp-2">{detail}</p>}
      {enabled ? <div className="mt-2">{children}</div> : <p className="mt-2 text-[11px] text-amber-300/80" data-testid={`${testId}-pending`}>{reason}</p>}
    </section>
  );
}

const Btn = ({ onClick, children, testId, title }) => (
  <button data-testid={testId} title={title} onClick={onClick}
    className="flex items-center justify-center gap-1 border border-cyan-500/30 bg-slate-900/80 px-2 py-1.5 text-[11px] text-cyan-100 hover:bg-cyan-500/15 hover:border-cyan-400 active:scale-95 transition-transform">
    {children}
  </button>
);

function TvTile({ run, enabled, st }) {
  const [apps, setApps] = useState([]);
  useEffect(() => { api.get("/devices").then((r) => setApps((r.data.find((d) => d.kind === "tv") || {}).apps || [])).catch(() => {}); }, []);
  const k = (key) => () => run("tv_key", { key });
  return (
    <Tile icon={Tv} title="TELE DEL CUARTO" testId="tile-tv" status={st.status} detail={st.detail} enabled={enabled}
      reason={enabled === false && st.status !== "desconocido" ? "Controles deshabilitados (modo verificación o ADB no disponible)." : "Requiere el agente Windows conectado con ADB disponible."}>
      <div className="grid grid-cols-4 gap-1">
        <Btn testId="tv-wake" onClick={k("wake")} title="Despertar"><Power className="h-3 w-3" /></Btn>
        <Btn testId="tv-sleep" onClick={k("sleep")} title="Suspender (no es apagado total)"><Moon className="h-3 w-3" /></Btn>
        <Btn testId="tv-home" onClick={k("home")} title="Inicio"><HomeIcon className="h-3 w-3" /></Btn>
        <Btn testId="tv-back" onClick={k("back")} title="Atrás"><Undo2 className="h-3 w-3" /></Btn>
        <Btn testId="tv-vol-down" onClick={k("vol_down")} title="Bajar volumen"><Volume1 className="h-3 w-3" /></Btn>
        <Btn testId="tv-vol-up" onClick={k("vol_up")} title="Subir volumen"><Volume2 className="h-3 w-3" /></Btn>
        <Btn testId="tv-mute" onClick={k("mute")} title="Silencio"><VolumeX className="h-3 w-3" /></Btn>
        <Btn testId="tv-enter" onClick={k("enter")} title="Aceptar"><CircleDot className="h-3 w-3" /></Btn>
        <Btn testId="tv-left" onClick={k("left")}><ChevronLeft className="h-3 w-3" /></Btn>
        <Btn testId="tv-up" onClick={k("up")}><ChevronUp className="h-3 w-3" /></Btn>
        <Btn testId="tv-down" onClick={k("down")}><ChevronDown className="h-3 w-3" /></Btn>
        <Btn testId="tv-right" onClick={k("right")}><ChevronRight className="h-3 w-3" /></Btn>
      </div>
      <TvRemote run={run} />
      <div className="mt-1 flex flex-wrap gap-1">
        {apps.slice(0, 6).map((a) => <Btn key={a.package} testId={`tv-app-${a.package}`} onClick={() => run("tv_open_app", { package: a.package })}>{a.label}</Btn>)}
        {apps.length === 0 && <span className="text-[10px] text-slate-500">Apps: consúltelas en Ajustes › Dispositivos.</span>}
      </div>
    </Tile>
  );
}

function LightsTile({ run, st }) {
  const [status, setStatus] = useState(null);
  const [devices, setDevices] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState({});
  const refresh = async () => {
    try {
      const res = await api.get("/ewelink/status");
      setStatus(res.data);
      if (res.data.status === "conectado") {
        const d = await api.get("/ewelink/devices");
        setDevices(d.data);
      } else setDevices([]);
      setError("");
    } catch (e) { setError(e?.response?.data?.detail || "No se pudo consultar eWeLink"); }
  };
  useEffect(() => { refresh(); const timer = setInterval(refresh, 30000); return () => clearInterval(timer); }, []);
  const authorize = async () => {
    setBusy(true);
    try {
      const {data} = await api.get("/ewelink/authorize");
      if (!data.url || !/^https:\/\//.test(data.url)) throw new Error("URL OAuth inválida");
      window.location.assign(data.url);
    } catch (e) { setError(e?.response?.data?.detail || e.message || "No se pudo iniciar autorización"); setBusy(false); }
  };
  const control = async (device, on) => {
    setBusy(true);
    try {
      const result = await run("lights_set", { target: device.id, on });
      if (result?.status !== "confirmada") throw new Error("Orden pendiente o sin confirmar. Revisá las aprobaciones.");
      await refresh();
    } catch (e) { setError(e.message || "No se pudo confirmar el estado"); }
    finally { setBusy(false); }
  };
  const saveMeta = async d => {
    setBusy(true);
    try {
      await api.put(`/ewelink/devices/${encodeURIComponent(d.id)}/meta`, {alias: editing[d.id]?.alias ?? d.alias ?? "", room: editing[d.id]?.room ?? d.room ?? ""});
      await refresh();
    } catch (e) { setError(e?.response?.data?.detail || "No se pudo guardar el nombre"); }
    finally { setBusy(false); }
  };
  const unlink = async () => {
    if (!window.confirm("¿Desvincular tu cuenta eWeLink de JARVIS?")) return;
    setBusy(true);
    try { await api.delete("/ewelink/authorization"); await refresh(); }
    catch (e) { setError(e?.response?.data?.detail || "No se pudo desvincular"); }
    finally { setBusy(false); }
  };
  const connected = status?.status === "conectado";
  return <Tile icon={Lightbulb} title="EWeLINK · LUCES" testId="tile-lights" status={connected ? "conectado" : "desconectado"} enabled={true} detail={status?.detail || st.detail}>
    <div className="space-y-3">
      <p className="text-xs text-slate-400">{connected ? "Cuenta autorizada. Controlá únicamente tus dispositivos vinculados." : status?.detail || "Consultando conexión…"}</p>
      <div className="flex flex-wrap gap-2">
        <Btn onClick={refresh}>Actualizar dispositivos</Btn>
        {!connected && <button disabled={busy} className="border border-cyan-500/40 px-2 py-1 text-xs text-cyan-200 disabled:opacity-50" onClick={authorize}>Conectar eWeLink</button>}
        {connected && <button disabled={busy} className="border border-amber-500/40 px-2 py-1 text-xs text-amber-200 disabled:opacity-50" onClick={unlink}>Desvincular</button>}
      </div>
      {connected && devices.map(d => <div key={d.id} className="border border-cyan-500/20 p-2 space-y-2">
        <div className="flex justify-between gap-2 text-xs"><strong>{d.alias || d.name || d.id}</strong><span>{!d.online ? "Sin conexión" : d.switch === "on" ? "Encendida" : d.switch === "off" ? "Apagada" : "Estado desconocido"}</span></div>
        <p className="text-[10px] text-slate-500">{d.room || "Sin habitación"} · {d.name || d.id}</p>
        <div className="grid grid-cols-2 gap-2">{[true,false].map(on=><button key={String(on)} disabled={busy || !d.online} onClick={()=>control(d,on)} className="border border-cyan-500/30 bg-slate-900 px-2 py-2 text-xs disabled:opacity-40">{on ? "Encender" : "Apagar"}</button>)}</div>
        <details><summary className="cursor-pointer text-xs text-cyan-300">Editar nombre y habitación</summary>
          <div className="space-y-2 mt-2">
            <input aria-label="Alias" placeholder="Nombre" maxLength={80} value={editing[d.id]?.alias ?? d.alias ?? ""} onChange={e=>setEditing(v=>({...v,[d.id]:{...v[d.id],alias:e.target.value}}))} className="w-full bg-slate-900 border border-cyan-500/30 p-2 text-xs"/>
            <input aria-label="Habitación" placeholder="Habitación" maxLength={80} value={editing[d.id]?.room ?? d.room ?? ""} onChange={e=>setEditing(v=>({...v,[d.id]:{...v[d.id],room:e.target.value}}))} className="w-full bg-slate-900 border border-cyan-500/30 p-2 text-xs"/>
            <Btn onClick={()=>saveMeta(d)}>Guardar</Btn>
          </div>
        </details>
      </div>)}
      {connected && !devices.length && <p className="text-xs text-slate-500">No se encontraron dispositivos en la cuenta.</p>}
      {error && <p className="text-xs text-amber-300">{String(error)}</p>}
    </div>
  </Tile>;
}

function PcTile({ run, agent, safe }) {
  const aliases = agent?.capabilities?.programs?.aliases || [];
  const [windows, setWindows] = useState([]);
  const [windowError, setWindowError] = useState("");
  const refreshWindows = async () => {
    const result = await run("pc_list_windows");
    if (result?.status === "confirmada" && Array.isArray(result.result?.windows)) {
      setWindows(result.result.windows);
      setWindowError("");
    } else setWindowError("No se pudo obtener la lista de ventanas. Revisá el agente.");
  };
  return (
    <Tile icon={Monitor} title="PC" testId="tile-pc" status={agent ? "conectado" : "desconectado"} detail={agent ? agent.name : "Agente Windows no conectado"}
      enabled={!!agent && !safe} reason={safe ? "Modo verificación: solo diagnósticos." : "Empareje e inicie el agente Windows (Ajustes › Agente)."}>
      <div className="grid grid-cols-3 gap-1">
        <Btn testId="pc-status" onClick={() => run("pc_system_status")}><Activity className="h-3 w-3" />Estado</Btn>
        <Btn testId="pc-windows" onClick={() => run("pc_list_windows")}><AppWindow className="h-3 w-3" />Ventanas</Btn>
        <Btn testId="pc-play" onClick={() => run("pc_media", { action: "play_pause" })}><Play className="h-3 w-3" />Media</Btn>
        <Btn testId="pc-volume-down" title="Bajar volumen de Windows" onClick={() => run("pc_media", { action: "vol_down" })}><Volume1 className="h-3 w-3" />Vol −</Btn>
        <Btn testId="pc-volume-up" title="Subir volumen de Windows" onClick={() => run("pc_media", { action: "vol_up" })}><Volume2 className="h-3 w-3" />Vol +</Btn>
        <Btn testId="pc-volume-mute" title="Silenciar Windows" onClick={() => run("pc_media", { action: "mute" })}><VolumeX className="h-3 w-3" />Silencio</Btn>
        {aliases.slice(0, 6).map((a) => <Btn key={a} testId={`pc-open-${a}`} onClick={() => run("pc_open_program", { alias: a })}>{a}</Btn>)}
      </div>
      <div className="mt-3 rounded border border-cyan-500/20 p-2" data-testid="pc-window-manager">
        <div className="mb-2 flex items-center justify-between gap-2">
          <strong className="text-xs text-cyan-100">VENTANAS ABIERTAS</strong>
          <Btn testId="pc-refresh-windows" onClick={refreshWindows}>Actualizar</Btn>
        </div>
        {windowError && <p className="text-xs text-amber-300">{windowError}</p>}
        <div className="max-h-60 space-y-2 overflow-y-auto">
          {windows.map(w => <div key={w.id} className="flex items-center justify-between gap-2 border border-cyan-500/15 p-2">
            <div className="min-w-0"><p className="truncate text-xs text-slate-100" title={w.title}>{w.title}</p><p className="truncate text-[10px] text-slate-500">{w.process || "Aplicación"} · PID {w.pid}</p></div>
            <div className="flex shrink-0 gap-1">
              <Btn testId={`pc-focus-${w.id}`} onClick={() => run("pc_focus_window", { window_id: w.id })}>Ver</Btn>
              <Btn testId={`pc-close-${w.id}`} title="Solicita cerrar; puede pedir guardar cambios" onClick={async () => { await run("pc_close_window", { window_id: w.id }); }}>Cerrar</Btn>
            </div>
          </div>)}
        </div>
        {!windows.length && !windowError && <p className="text-[11px] text-slate-500">Tocá Actualizar para consultar las ventanas reales de Windows.</p>}
      </div>
      <PcRemote run={run} />
      <YouTubeMusic run={run} supported={!!agent?.capabilities?.music_sessions?.available} />
    </Tile>
  );
}

function PcFilesTile({run, enabled}) {
  const [query, setQuery] = useState("");
  const [folder, setFolder] = useState("");
  const [path, setPath] = useState("");
  const [result, setResult] = useState(null);
  const invoke = async (tool, args) => {
    const response = await run(tool, args);
    setResult(response?.status === "confirmada" ? response.result : {error: response?.meaning || "No se pudo confirmar la operación"});
  };
  return <Tile icon={AppWindow} title="ARCHIVOS AUTORIZADOS" testId="tile-pc-files" status={enabled ? "conectado" : "desconectado"} enabled={enabled} reason="Conectá el agente Windows para consultar carpetas autorizadas.">
    <div className="space-y-2">
      <p className="text-xs text-slate-400">Búsqueda y lectura dentro de carpetas permitidas. Esta sección no modifica archivos.</p>
      <input aria-label="Buscar archivos" value={query} onChange={e=>setQuery(e.target.value)} maxLength={120} placeholder="Buscar por nombre" className="w-full border border-cyan-500/30 bg-slate-950 p-2 text-xs" />
      <input aria-label="Alias de carpeta" value={folder} onChange={e=>setFolder(e.target.value)} maxLength={60} placeholder="Alias de carpeta (opcional)" className="w-full border border-cyan-500/30 bg-slate-950 p-2 text-xs" />
      <Btn testId="pc-files-search" onClick={()=>query.trim()&&invoke("pc_search_files",{query:query.trim(),...(folder.trim()?{folder_alias:folder.trim()}: {})})}>Buscar</Btn>
      <input aria-label="Ruta de archivo" value={path} onChange={e=>setPath(e.target.value)} maxLength={500} placeholder="Ruta completa de archivo autorizado" className="w-full border border-cyan-500/30 bg-slate-950 p-2 text-xs" />
      <Btn testId="pc-files-read" onClick={()=>path.trim()&&invoke("pc_read_file",{path:path.trim()})}>Leer archivo</Btn>
      {result&&<pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all border border-cyan-500/20 p-2 text-[11px]">{JSON.stringify(result,null,2)}</pre>}
    </div>
  </Tile>;
}

function AlexaTile() {
  const [status, setStatus] = useState(null);
  const [commands, setCommands] = useState([]);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const refresh = async () => {
    try {
      const [a, b] = await Promise.all([api.get("/alexa/status"), api.get("/alexa/commands")]);
      setStatus(a.data);
      setCommands(b.data.items || []);
      setError("");
    } catch (e) { setError(e?.response?.data?.detail || "No se pudo consultar Alexa"); }
  };
  useEffect(() => { refresh(); }, []);
  const pair = async () => {
    setBusy(true);
    try { const r = await api.post("/alexa/pair"); setCode(r.data.code); setError(""); }
    catch (e) { setError(e?.response?.data?.detail || "No se pudo generar el código"); }
    finally { setBusy(false); }
  };
  const unlink = async () => {
    if (!window.confirm("¿Desvincular Alexa de JARVIS?")) return;
    setBusy(true);
    try { await api.delete("/alexa/link"); setCode(""); await refresh(); }
    catch (e) { setError(e?.response?.data?.detail || "No se pudo desvincular"); }
    finally { setBusy(false); }
  };
  return <Tile icon={HomeIcon} title="ALEXA" testId="tile-alexa" status={status?.linked ? "conectado" : "desconectado"} enabled={true}>
    <div className="space-y-2 text-xs">
      <p className="text-slate-300">{status?.linked ? "Cuenta Alexa vinculada" : "Vinculá tu cuenta de Alexa con un código temporal."}</p>
      <div className="flex flex-wrap gap-2">
        <Btn onClick={refresh}>Actualizar</Btn>
        {!status?.linked && <button disabled={busy} className="border border-cyan-500/40 px-2 py-1 text-cyan-200 disabled:opacity-50" onClick={pair}>Generar código</button>}
        {status?.linked && <button disabled={busy} className="border border-amber-500/40 px-2 py-1 text-amber-200 disabled:opacity-50" onClick={unlink}>Desvincular</button>}
      </div>
      {code && <div className="border border-cyan-500/30 p-2"><p className="font-mono text-lg tracking-widest text-cyan-200">{code}</p><p className="text-slate-400">Vence en 10 minutos. Decí a Alexa: «abre Jarvis» y luego «vincula código» seguido de estos seis números.</p></div>}
      {error && <p className="text-amber-300">{String(error)}</p>}
      <details><summary className="cursor-pointer text-cyan-300">Órdenes disponibles ({commands.length})</summary><div className="max-h-36 overflow-y-auto space-y-1 mt-2">{commands.map((c,i)=><p key={i} className="text-slate-400">{c.example || c.description || c.tool}</p>)}</div></details>
      <p className="text-slate-500">Las acciones sensibles pueden requerir aprobación en JARVIS.</p>
    </div>
  </Tile>;
}

function ObsTile({ run, enabled, st }) {
  const [scenes, setScenes] = useState(null);
  const load = async () => { const s = await run("obs_list_scenes"); if (s?.status === "confirmada") setScenes(s.result.scenes || []); };
  return (
    <Tile icon={Radio} title="OBS" testId="tile-obs" status={st.status} detail={st.detail} enabled={enabled}
      reason="Requiere el agente conectado y OBS WebSocket habilitado (Herramientas › Ajustes de WebSocket).">
      <div className="grid grid-cols-2 gap-1">
        <Btn testId="obs-status" onClick={() => run("obs_status")}>Estado</Btn>
        <Btn testId="obs-scenes" onClick={load}>Ver escenas</Btn>
        <Btn testId="obs-start" onClick={() => run("obs_start_stream")}>Iniciar stream</Btn>
        <Btn testId="obs-stop" onClick={() => run("obs_stop_stream")}>Detener stream</Btn>
      </div>
      {scenes && <div className="mt-1 flex flex-wrap gap-1">{scenes.map((s) => <Btn key={s} testId={`obs-scene-${s}`} onClick={() => run("obs_set_scene", { scene: s })}>{s}</Btn>)}</div>}
    </Tile>
  );
}

export function DeviceTiles({ run, only }) {
  const scope=["tv","pc"].includes(only)?only:null;
  const { onlineAgent, status } = useLive();
  const caps = onlineAgent?.capabilities || {};
  const safe = !!caps.safe_mode;
  return (
    <div className="space-y-3">
    {!scope&&<OperationsPanels />}
    {!scope&&<TradingOS embedded />}
    {safe && <p className="hud-panel p-2 text-[11px] text-sky-300" data-testid="safe-mode-banner">Agente en MODO VERIFICACIÓN: controles deshabilitados hasta verificar agente, tele y OBS (Ajustes › Agente) y desactivar safe_mode.</p>}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3" data-testid="device-tiles">
      {scope!=="pc"&&<TvTile run={run} enabled={!!onlineAgent && caps.adb?.available && !safe} st={status("adb")} />}
      {!scope&&<LightsTile run={run} st={status("ewelink")} />}
      {scope!=="tv"&&<PcTile run={run} agent={onlineAgent} safe={safe} />}
      {scope!=="tv"&&<PcFilesTile run={run} enabled={!!onlineAgent && !safe} />}
      {!scope&&<AlexaTile />}
      {scope!=="tv"&&<ObsTile run={run} enabled={!!onlineAgent && caps.obs?.available && !safe && status("obs").status === "conectado"} st={status("obs")} />}
    </div>
    </div>
  );
}
